import { Component, Vue } from 'vue-property-decorator';

/** Seconds a resend button stays disabled. Client-side only. */
export const RESEND_COOLDOWN_SECONDS = 60;

/**
 * A countdown that keeps a "resend the email" button disabled for a while.
 *
 * Purely cosmetic - the server is the real rate limiter and answers 429 - but
 * it stops a user hammering the button and mailing themselves a dozen links.
 *
 * The remaining time is derived from a deadline rather than decremented on a
 * tick, so a throttled background tab cannot stretch 59 seconds into minutes.
 */
@Component
export default class ResendCooldown extends Vue {
  /** Epoch ms at which the cooldown ends; 0 when not counting down. */
  cooldownEndsAt: number = 0;

  /** Epoch ms, refreshed by the ticker purely to drive re-rendering. */
  cooldownNow: number = 0;

  cooldownTimer: number | null = null;

  /** Whole seconds left, 0 when the button is usable again. */
  get cooldownRemaining(): number {
    if (!this.cooldownEndsAt) {
      return 0;
    }
    const left = this.cooldownEndsAt - this.cooldownNow;
    return Math.max(0, Math.ceil(left / 1000));
  }

  get cooldownActive(): boolean {
    return this.cooldownRemaining > 0;
  }

  startCooldown(seconds: number = RESEND_COOLDOWN_SECONDS) {
    this.stopCooldown();
    this.cooldownNow = Date.now();
    this.cooldownEndsAt = this.cooldownNow + seconds * 1000;
    this.cooldownTimer = window.setInterval(() => {
      this.cooldownNow = Date.now();
      if (!this.cooldownActive) {
        this.stopCooldown();
      }
    }, 250);
  }

  stopCooldown() {
    if (this.cooldownTimer !== null) {
      window.clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }

  beforeDestroy() {
    this.stopCooldown();
  }
}
